import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Building2, X, Copy, Check, FileText, Send, Eye, Plus, FlaskConical } from 'lucide-react';

const API = '/api';

// Facility partner onboarding (Randall, 30 Sep 2026) — one screen, lead to active.
//
// The facility agreement is the fitter license's system with a second agreement type: the
// signing link opens the same signing page, the card step is the same Stax page, and
// countersigning is the same approval. What this screen adds is the facility record and the
// order of the rail: Draft → Sent → Signed → Paid → Countersigned → Active.
//
// NOTHING ON THIS SCREEN SENDS BY ITSELF. Every email the fitter flow sends automatically is a
// Send button below, and each one previews first. "Paid" is Randall's mark after he charges
// the card in the Stax dashboard — the Hub never charges a card.

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return String(iso).slice(0, 16); }
};

const RAIL_LABEL = { draft: 'Draft', sent: 'Sent', signed: 'Signed', paid: 'Paid',
  countersigned: 'Countersigned', active: 'Active', declined: 'Declined' };

const EMAILS = [
  { kind: 'invite', label: 'Signing link', when: 'fitter flow: at invite' },
  { kind: 'confirmation', label: 'Signed copy to the facility', when: 'fitter flow: at signing' },
  { kind: 'backup', label: 'Internal copy (randall@ / grant@)', when: 'fitter flow: at signing' },
  { kind: 'card_link', label: 'Card-setup link', when: 'fitter flow: at approval' },
  { kind: 'executed', label: 'Fully-executed copy', when: 'fitter flow: at approval' },
];

const btn = 'inline-flex items-center gap-1.5 px-3 py-1 text-xs font-sans border rounded-sm disabled:opacity-40';
const btnGreen = `${btn} border-hg-green text-hg-green`;
const btnDim = `${btn} border-hg-border text-hg-text-dim hover:border-hg-green hover:text-hg-green`;
const input = 'bg-hg-bg border border-hg-border rounded-sm text-[13px] text-hg-text px-2 py-1 font-sans';
const sectionHead = 'text-[10px] uppercase tracking-wider text-hg-text-dim font-sans';

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  return typeof d === 'string' ? d : (d.message || fallback);
}

function Rail({ status }) {
  const order = ['draft', 'sent', 'signed', 'paid', 'countersigned', 'active'];
  const at = order.indexOf(status);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="facility-rail">
      {order.map((s, i) => (
        <span key={s} className={`px-2 py-0.5 text-[11px] font-sans rounded-sm border ${
          i < at ? 'border-hg-green/40 text-hg-green/70'
            : i === at ? 'border-hg-green bg-hg-green/15 text-hg-green font-semibold'
              : 'border-hg-border text-hg-text-dim'}`}>
          {RAIL_LABEL[s]}
        </span>
      ))}
      {status === 'declined' && <span className="px-2 py-0.5 text-[11px] font-sans rounded-sm border border-red-400 text-red-400">Declined</span>}
    </div>
  );
}

function PrefillForm({ f, onSaved, setError }) {
  const p = f.prefill || {};
  const [v, setV] = useState(p);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(f.prefill || {}); }, [f]);
  const editable = f.status === 'draft';
  const fields = [
    ['business_name', 'Facility legal name'], ['dba', 'Doing business as'],
    ['contact_name', 'Owner / authorized signer'], ['signer_title', 'Title'],
    ['email', 'Email'], ['phone', 'Phone'], ['address_line1', 'Street address'],
    ['city', 'City'], ['state', 'State'], ['postal_code', 'Zip'], ['country', 'Country'],
  ];
  const save = async () => {
    setBusy(true); setError('');
    try {
      const body = {};
      fields.forEach(([k]) => { if ((v[k] || '') !== (p[k] || '')) body[k] = v[k] || ''; });
      if ((v.facility_type || '') !== (p.facility_type || '')) body.facility_type = v.facility_type || '';
      const r = await axios.post(`${API}/partners/facilities/${f.id}/prefill`, body);
      onSaved(r.data);
    } catch (e) { setError(errText(e, 'Not saved.')); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className={sectionHead}>Schedule — prefilled for the signing form {editable ? '' : '(locked once the link is out)'}</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {fields.map(([k, label]) => (
          <label key={k} className="text-[11px] text-hg-text-dim font-sans flex flex-col gap-0.5">{label}
            <input className={input} value={v[k] || ''} disabled={!editable}
              onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`prefill-${k}`} />
          </label>
        ))}
        <label className="text-[11px] text-hg-text-dim font-sans flex flex-col gap-0.5">Facility type
          <select className={input} value={v.facility_type || ''} disabled={!editable}
            onChange={(e) => setV({ ...v, facility_type: e.target.value })}>
            <option value="">—</option>
            {['Indoor venue', 'Course', 'Range', 'Academy'].map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
      </div>
      {editable && <button className={btnGreen} onClick={save} disabled={busy}>{busy ? <Loader2 size={12} className="animate-spin" /> : 'Save schedule'}</button>}
    </div>
  );
}

function DesignatedFitter({ f, onSaved, setError }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [mode, setMode] = useState('existing');
  const [nf, setNf] = useState({ name: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (mode !== 'existing' || q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(async () => {
      try { const r = await axios.get(`${API}/partners/facilities/fitter-search`, { params: { q } }); setHits(r.data.fitters || []); }
      catch { setHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, mode]);
  const link = async (body) => {
    setBusy(true); setError('');
    try { const r = await axios.post(`${API}/partners/facilities/${f.id}/designated-fitter`, body); onSaved(r.data); setQ(''); }
    catch (e) { setError(errText(e, 'Fitter not linked.')); }
    setBusy(false);
  };
  const cur = f.designated_fitter;
  return (
    <div className="space-y-2" data-testid="designated-fitter">
      <div className={sectionHead}>Designated Fitter (coach)</div>
      {cur ? (
        <div className="text-[12px] font-sans text-hg-text">
          {cur.name} · {cur.primary_email} · {cur.status}
          <span className="text-hg-text-dim"> — {f.designated_fitter_link === 'new' ? `new record, CoFitter certification within ${f.certification_window_days} days of the system arriving` : 'from the roster'}</span>
        </div>
      ) : <div className="text-[12px] font-sans text-hg-text-dim">Not linked yet.</div>}
      <div className="flex gap-2 text-[11px] font-sans">
        <button className={mode === 'existing' ? btnGreen : btnDim} onClick={() => setMode('existing')}>Pick from roster</button>
        <button className={mode === 'new' ? btnGreen : btnDim} onClick={() => setMode('new')}>New fitter record</button>
      </div>
      {mode === 'existing' ? (
        <div className="space-y-1">
          <input className={`${input} w-full`} placeholder="Search fitters by name or email (e.g. Bruce Lewis)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fitter-search" />
          {hits.map((h) => (
            <button key={h.id} disabled={busy} onClick={() => link({ mode: 'existing', fitter_id: h.id })}
              className="block w-full text-left text-[12px] font-sans px-2 py-1 border border-hg-border rounded-sm hover:border-hg-green text-hg-text">
              {h.name} <span className="text-hg-text-dim">· {h.primary_email} · {h.status}{h.is_test ? ' · test' : ''}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap">
          <input className={input} placeholder="Name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} />
          <input className={input} placeholder="Email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} />
          <input className={input} placeholder="Phone" value={nf.phone} onChange={(e) => setNf({ ...nf, phone: e.target.value })} />
          <button className={btnGreen} disabled={busy} onClick={() => link({ mode: 'new', ...nf })}>Create (no emails)</button>
        </div>
      )}
    </div>
  );
}

function DeleteTest({ f, onDeleted, setError }) {
  // TEST facilities only — the server refuses a real one whatever this button does. Two clicks,
  // inline, rather than a browser confirm() (Phase 4: no alert/confirm dialogs).
  const [arm, setArm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!f.is_test) return null;
  const go = async () => {
    setBusy(true); setError('');
    try { await axios.delete(`${API}/partners/facilities/${f.id}/test-record`); onDeleted(); }
    catch (e) { setError(errText(e, 'Not deleted.')); }
    setBusy(false);
  };
  return arm ? (
    <span className="inline-flex items-center gap-2 text-[12px] font-sans" data-testid="delete-test-confirm">
      <span className="text-red-400">Delete this test facility, its agreement and any fitter it created?</span>
      <button className={`${btn} border-red-400 text-red-400`} disabled={busy} onClick={go} data-testid="delete-test-yes">Yes, delete</button>
      <button className={btnDim} onClick={() => setArm(false)}>Cancel</button>
    </span>
  ) : (
    <button className={`${btn} border-hg-border text-hg-text-dim hover:border-red-400 hover:text-red-400`} onClick={() => setArm(true)} data-testid="delete-test">Delete test facility</button>
  );
}

function Detail({ id, onClose, onChanged }) {
  const [f, setF] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);
  const [radius, setRadius] = useState(15);
  const [staxRef, setStaxRef] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { const r = await axios.get(`${API}/partners/facilities/${id}`); setF(r.data); }
    catch (e) { setError(errText(e, 'Could not load the facility.')); }
  }, [id]);
  useEffect(() => { load(); setPreview(null); }, [load]);

  const act = async (label, fn) => {
    setBusy(label); setError('');
    try { const r = await fn(); if (r?.data?.id) setF(r.data); else await load(); onChanged?.(); }
    catch (e) { setError(errText(e, `${label} failed.`)); }
    setBusy('');
  };

  const openPdf = async () => {
    try {
      const r = await axios.get(`/api/agreements/${f.agreement.agreement_id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
    } catch (e) { setError('Could not open the signed PDF.'); }
  };

  const showEmail = async (kind) => {
    setPreview({ kind, html: null, error: '' });
    try {
      const r = await axios.post(`${API}/partners/facilities/${id}/send/${kind}`, { preview: true });
      setPreview({ kind, html: r.data.html, to: r.data.to, subject: r.data.subject, error: '' });
    } catch (e) { setPreview({ kind, html: null, error: errText(e, 'Cannot render.') }); }
  };
  const sendEmail = (kind) => act(`Send ${kind}`, async () => {
    await axios.post(`${API}/partners/facilities/${id}/send/${kind}`, { preview: false });
    setPreview(null);
  });

  if (!f) return <div className="p-4 text-xs text-hg-text-dim font-sans">{error || <Loader2 size={14} className="animate-spin" />}</div>;
  const copy = async () => {
    try { await navigator.clipboard.writeText(f.signing_link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* blocked */ }
  };
  const startUp = (f.line_items_display || []).find((i) => i.code === 'start_up_fee');

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-4" data-testid="facility-detail">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="font-display text-lg text-hg-text tracking-wide flex items-center gap-2">
            {f.display_name}
            {f.is_test && <span className="text-[10px] px-1.5 py-0.5 border border-amber-400 text-amber-300 rounded-sm font-sans">TEST — card simulated, emails to HG only</span>}
          </div>
          <div className="text-[12px] text-hg-text-dim font-sans">
            {f.reference ? `${f.reference} · ` : ''}Facility Partner Agreement · terms {f.terms_version}
            {f.lead ? ` · lead: ${f.lead.facility} (${f.lead.status})` : ' · started by hand'}
          </div>
          <Rail status={f.status} />
        </div>
        <button onClick={onClose} className="p-1 text-hg-text-dim hover:text-hg-text"><X size={16} /></button>
      </div>
      {error && <div className="text-[12px] text-red-400 font-sans" data-testid="facility-error">{error}</div>}

      <PrefillForm f={f} onSaved={setF} setError={setError} />
      <div className="border-t border-hg-border pt-3"><DesignatedFitter f={f} onSaved={setF} setError={setError} /></div>

      <div className="border-t border-hg-border pt-3 space-y-2">
        <div className={sectionHead}>Signing link</div>
        {f.signing_link ? (
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-[11px] text-hg-text bg-hg-bg border border-hg-border rounded-sm px-2 py-1 break-all" data-testid="signing-link">{f.signing_link}</code>
            <button className={btnDim} onClick={copy}>{copied ? <Check size={12} /> : <Copy size={12} />} Copy</button>
            <button className={btnDim} disabled={!!busy} onClick={() => act('Regenerate', () => axios.post(`${API}/partners/facilities/${id}/signing-link?regenerate=true`))}>Regenerate</button>
          </div>
        ) : ['draft', 'sent'].includes(f.status) ? (
          <button className={btnGreen} disabled={!!busy} data-testid="make-signing-link"
            onClick={() => act('Signing link', () => axios.post(`${API}/partners/facilities/${id}/signing-link`))}>
            {busy === 'Signing link' ? <Loader2 size={12} className="animate-spin" /> : 'Create signing link'}
          </button>
        ) : <div className="text-[12px] font-sans text-hg-text-dim">Signed {fmt(f.signed_at)}.</div>}
        <div className="text-[11px] text-hg-text-dim font-sans">Nothing is emailed when the link is made. Copy it, or use Send below. Links expire after 30 days; Regenerate issues a fresh one.</div>
      </div>

      <div className="border-t border-hg-border pt-3 space-y-2">
        <div className={sectionHead}>Signed agreement & card on file</div>
        <div className="flex items-center gap-3 flex-wrap text-[12px] font-sans text-hg-text">
          {f.signed_pdf_public_id
            ? <button className={btnDim} onClick={openPdf} data-testid="open-pdf"><FileText size={12} /> Open signed PDF{f.countersigned_at ? ' (fully executed)' : ''}</button>
            : <span className="text-hg-text-dim">No signed PDF yet.</span>}
          <span>Card: {f.card_on_file_at ? `${f.card_type || 'card'} ending ${f.card_last_four || '????'} · on file ${fmt(f.card_on_file_at)}` : `not on file (card link: ${f.card_link?.status || 'none'})`}</span>
          {f.agreement?.fee_choice && <span>Fee choice: {f.agreement.fee_choice}</span>}
        </div>
      </div>

      <div className="border-t border-hg-border pt-3 space-y-2" data-testid="line-items">
        <div className={sectionHead}>Payment — line items on the card on file</div>
        {(f.line_items_display || []).length === 0 ? (
          <div className="text-[12px] font-sans text-hg-text-dim">Built when the facility signs and picks monthly or annual.</div>
        ) : (
          <table className="w-full text-[12px] font-sans">
            <tbody>
              {f.line_items_display.map((i) => (
                <tr key={i.code} className="border-t border-hg-border/60">
                  <td className="py-1.5 text-hg-text">{i.label}</td>
                  <td className="py-1.5 text-hg-text">{i.amount}</td>
                  <td className="py-1.5 text-hg-text-dim">{i.schedule}{i.starts_on ? ` from ${i.starts_on}` : ` · ${i.due}`}</td>
                  <td className="py-1.5 text-right">
                    {i.charged_at
                      ? <span className="text-hg-green">charged {fmt(i.charged_at)}{i.stax_ref ? ` · ${i.stax_ref}` : ''}</span>
                      : <button className={btnGreen} disabled={!!busy || !f.card_on_file_at || (i.code === 'license_fee' && !f.effective_date)}
                          data-testid={`mark-${i.code}`}
                          onClick={() => act('Mark charged', () => axios.post(`${API}/partners/facilities/${id}/line-items/${i.code}/charged`, { stax_ref: staxRef || null }))}>
                          Mark charged in Stax</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {startUp && !startUp.charged_at && f.card_on_file_at && (
          <input className={`${input} w-64`} placeholder="Stax transaction id (optional)" value={staxRef} onChange={(e) => setStaxRef(e.target.value)} />
        )}
        <div className="text-[11px] text-hg-text-dim font-sans">The Hub never charges a card. Charge it in the Stax dashboard, then mark it here. Marking the start-up fee moves the facility to Paid.</div>
      </div>

      <div className="border-t border-hg-border pt-3 flex items-center gap-3 flex-wrap">
        <button className={btnGreen} disabled={!!busy || f.status !== 'paid'} data-testid="countersign"
          onClick={() => act('Countersign', () => axios.post(`${API}/partners/facilities/${id}/countersign`))}>
          {busy === 'Countersign' ? <Loader2 size={12} className="animate-spin" /> : 'Countersign (Randall Henry, CEO)'}
        </button>
        <span className="text-[11px] text-hg-text-dim font-sans">{f.countersigned_at ? `Countersigned ${fmt(f.countersigned_at)} · Effective Date ${f.effective_date}` : 'After Paid. This is the Effective Date; the license fee starts then.'}</span>
      </div>

      <div className="border-t border-hg-border pt-3 space-y-2" data-testid="activate">
        <div className={sectionHead}>Activate</div>
        {f.status === 'active' ? (
          <div className="text-[12px] font-sans text-hg-text">
            Active {fmt(f.activated_at)} · {f.exclusivity?.radius_miles}-mile exclusivity ({f.exclusivity?.basis}) from{' '}
            {[f.exclusivity?.location?.address_line1, f.exclusivity?.location?.city, f.exclusivity?.location?.state].filter(Boolean).join(', ')}
            {f.lead_won?.to ? ` · lead marked ${f.lead_won.to}` : ''}
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <select className={input} value={radius} onChange={(e) => setRadius(Number(e.target.value))} data-testid="radius">
              {(f.radius_choices || [15, 30]).map((m) => <option key={m} value={m}>{m} miles {m === 15 ? '(metro)' : '(elsewhere)'}</option>)}
            </select>
            <button className={btnGreen} disabled={!!busy || f.status !== 'countersigned'}
              onClick={() => act('Activate', () => axios.post(`${API}/partners/facilities/${id}/activate`, { radius_miles: radius }))}>Activate</button>
            <span className="text-[11px] text-hg-text-dim font-sans">Marks the partner lead won (no MailerLite change) and records the radius from the facility address.</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-[12px] font-sans">
          <span className="text-hg-text-dim">Launch kit:</span>
          <select className={input} value={f.launch_kit_slug || ''} data-testid="launch-kit"
            onChange={(e) => act('Launch kit', () => axios.post(`${API}/partners/facilities/${id}/launch-kit`, { slug: e.target.value || null }))}>
            <option value="">— none linked —</option>
            {(f.launch_kit_options || []).map((k) => <option key={k.slug} value={k.slug}>{k.name}</option>)}
          </select>
          <span className="text-hg-text-dim text-[11px]">Its keyed URL is in Settings → Marketing pack links.</span>
        </div>
      </div>

      <div className="border-t border-hg-border pt-3 space-y-2" data-testid="emails">
        <div className={sectionHead}>Emails — nothing sends until you press Send</div>
        {EMAILS.map((m) => {
          const n = (f.notifications || {})[m.kind];
          return (
            <div key={m.kind} className="flex items-center gap-2 flex-wrap text-[12px] font-sans">
              <span className="text-hg-text w-56">{m.label}</span>
              <span className="text-hg-text-dim text-[11px] w-40">{m.when}</span>
              <button className={btnDim} onClick={() => showEmail(m.kind)} data-testid={`preview-${m.kind}`}><Eye size={12} /> Preview</button>
              {n && <span className={n.status === 'sent' ? 'text-hg-green' : 'text-red-400'}>{n.status} {fmt(n.at)}{n.to ? ` → ${n.to.join(', ')}` : ''}</span>}
            </div>
          );
        })}
        {preview && (
          <div className="border border-hg-border rounded-sm">
            {preview.error ? <div className="p-3 text-[12px] text-red-400 font-sans">{preview.error}</div>
              : preview.html ? (
                <>
                  <div className="flex items-center justify-between gap-2 p-2 text-[12px] font-sans bg-hg-bg">
                    <span className="text-hg-text">To {preview.to?.join(', ')} · “{preview.subject}”</span>
                    <button className={btnGreen} disabled={!!busy} onClick={() => sendEmail(preview.kind)} data-testid="send-now"><Send size={12} /> Send now</button>
                  </div>
                  <iframe title={`Email ${preview.kind}`} srcDoc={preview.html} className="w-full h-[460px] bg-white" sandbox="" />
                </>
              ) : <div className="p-3"><Loader2 size={14} className="animate-spin" /></div>}
          </div>
        )}
      </div>

      {f.is_test && (
        <div className="border-t border-hg-border pt-3">
          <DeleteTest f={f} setError={setError} onDeleted={() => { onChanged?.(); onClose(); }} />
        </div>
      )}

      {(f.status_history || []).length > 0 && (
        <div className="border-t border-hg-border pt-3 text-[11px] font-sans text-hg-text-dim space-y-0.5">
          {[...f.status_history].reverse().slice(0, 8).map((h, i) => (
            <div key={i}>{fmt(h.at)} · {RAIL_LABEL[h.status] || h.status} · {h.by}{h.note ? ` — ${h.note}` : ''}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FacilityOnboarding({ openId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(openId || null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await axios.get(`${API}/partners/facilities`); setData(r.data); }
    catch (e) { setError(errText(e, 'Could not load facilities.')); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (openId) setOpen(openId); }, [openId]);

  const create = async (test) => {
    setError('');
    try {
      const r = await axios.post(`${API}/partners/facilities${test ? '/test' : ''}`, test ? { business_name: 'Test Golf Lounge' } : {});
      setOpen(r.data.id); load();
    } catch (e) { setError(errText(e, 'Not created.')); }
  };

  return (
    <div className="space-y-4" data-testid="facility-onboarding-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">FACILITY ONBOARDING</h1>
          <p className="text-sm text-hg-text-dim font-sans mt-1">
            Facility Partner Agreement, lead to active. Start one from a partner lead (Facility Partners → a lead → Start onboarding), or by hand.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className={btnDim} onClick={() => create(false)}><Plus size={12} /> New facility</button>
          <button className={btnDim} onClick={() => create(true)} data-testid="new-test-facility"><FlaskConical size={12} /> New test facility</button>
          <button onClick={load} disabled={loading} className={btnDim}>
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
          </button>
        </div>
      </div>
      {error && <div className="text-xs text-red-400 font-sans">{error}</div>}
      {open && <Detail id={open} onClose={() => setOpen(null)} onChanged={load} />}
      {data && (
        <div className="overflow-x-auto border border-hg-border rounded-sm">
          <table className="w-full text-[12px] font-sans">
            <thead className="bg-hg-surface text-hg-text-dim text-[10px] uppercase tracking-wider">
              <tr>{['Facility', 'Where', 'Status', 'Reference', 'Updated'].map((h) => (
                <th key={h} className="text-left px-2 py-2 font-normal whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody>
              {data.facilities.map((r) => (
                <tr key={r.id} onClick={() => setOpen(r.id)} data-testid={`facility-row-${r.id}`}
                  className={`border-t border-hg-border/60 cursor-pointer hover:bg-hg-surface ${open === r.id ? 'bg-hg-surface' : ''}`}>
                  <td className="px-2 py-1.5 text-hg-text inline-flex items-center gap-1.5"><Building2 size={11} className="text-hg-green" />{r.display_name}{r.is_test ? ' (test)' : ''}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-2 py-1.5 text-hg-text whitespace-nowrap">{RAIL_LABEL[r.status] || r.status}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{r.reference || '—'}</td>
                  <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{fmt(r.updated_at)}</td>
                </tr>
              ))}
              {data.facilities.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-6 text-center text-hg-text-dim italic">No facility onboardings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
